import { Badge } from 'components/badges';
import { DotIcon } from 'libs/icons/svg-icons/svgs/dot';
import { FixedDate } from 'libs/time';
import { useTranslation } from 'libs/translations';
import { useMemo } from 'react';
import { AutoshipStatusType } from 'types/business-data/IAutoship';

import {
  AUTOSHIP_BADGE_BACKGROUND_COLORS,
  AUTOSHIP_BADGE_CONTENT_COLORS
} from './constants';

interface IAutoshipBadgeProps {
  readonly status: AutoshipStatusType;
  readonly style: 'date' | 'status';
  readonly hideDetails?: boolean;
  readonly lastExecutionDate?: FixedDate;
  readonly nextExecutionDate?: FixedDate;
}

export function AutoshipBadge(props: IAutoshipBadgeProps) {
  const { t } = useTranslation();
  const { style, lastExecutionDate, nextExecutionDate, status, hideDetails } =
    props;

  const nextPointsDay = useMemo(() => {
    if (!lastExecutionDate || !nextExecutionDate) {
      return '';
    }
    // If autoship is processed, we extract the day based on the last execution date, because next execution date can be modified by the user
    if (status === 'processed') {
      return `${lastExecutionDate?.day}.${lastExecutionDate?.month}`;
    }

    if (status === 'paused') {
      return `${nextExecutionDate?.day}.${nextExecutionDate?.month}`;
    }

    return `${nextExecutionDate?.day}.`;
  }, [lastExecutionDate, nextExecutionDate, status]);

  const labels = {
    processed: t('Processed'),
    notProcessed: t('Scheduled'),
    paused: t('Paused'),
    cancelled: t('Canceled')
  };

  const title = style === 'date' ? nextPointsDay : labels[status];

  return (
    <Badge
      icon={<DotIcon size={6} />}
      title={title}
      backgroundColorClass={AUTOSHIP_BADGE_BACKGROUND_COLORS[status]}
      textColorClass={AUTOSHIP_BADGE_CONTENT_COLORS[status]}
      tooltipDetailsContent={<AutoshipStatusHelper />}
      hideDetails={hideDetails}
    />
  );
}

function AutoshipStatusHelper() {
  const { t } = useTranslation();

  return (
    <>
      <h1>{t('Autoship status')}</h1>
      <ul className="space-y-sm">
        <li>
          <AutoshipBadge style="status" hideDetails status="processed" />
          <p>{t('Autoship was processed and points were credited.')}</p>
        </li>
        <li>
          <AutoshipBadge style="status" hideDetails status="notProcessed" />
          <p>
            {t(
              'Autoship will be processed and points credited later in the specified month.'
            )}
          </p>
        </li>
        <li>
          <AutoshipBadge style="status" hideDetails status="paused" />
          <p>
            {t(
              'Autoship was paused and will not be processed in the specified month.'
            )}
          </p>
        </li>
        <li>
          <AutoshipBadge style="status" hideDetails status="cancelled" />
          <p>{t('Autoship is canceled.')}</p>
        </li>
      </ul>
    </>
  );
}
